import React, { useMemo, useState } from "react";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";

import CustomDropdown from "../../components/CustomDropdown";
import DatePicker from "../../components/DatePicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { subDays } from "date-fns";
import { getDateRange, getFriendlyDateRangeI18nKey, getFriendlyDateRangeKey } from "../../helpers/DateTime";

import SiteStatisticsFilter from "./SiteStatisticsFilter";
import SiteStatisticsContent from "./SiteStatisticsContent";
import { useTranslation } from "react-i18next";

const COOKIE_KEY = "siteStatisticsDashboardFilters_v1";

// minimal cookie read (same key as filter uses)
function getCookie(name) {
    const key = encodeURIComponent(name) + "=";
    const parts = document.cookie.split("; ");
    for (const part of parts) {
        if (part.startsWith(key)) return decodeURIComponent(part.slice(key.length));
    }
    return null;
}

export default function SiteStatisticsDashboard() {
    const { t } = useTranslation();
    const [sitePublicIds, setSitePublicIds] = useState([]);
    const [isLoadingSites, setIsLoadingSites] = useState(true);

    // Read cookie once to restore dates (optional)
    const saved = useMemo(() => {
        try {
            const raw = getCookie(COOKIE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }, []);

    const initialEnd = saved?.endDate ? new Date(saved.endDate) : new Date();
    const initialStart = saved?.startDate
        ? new Date(saved.startDate)
        : subDays(initialEnd, 7);

    const [startDate, setStartDate] = useState(initialStart);
    const [endDate, setEndDate] = useState(initialEnd);

    const getDaySelectionClass = () => {
        const rangeKey = getFriendlyDateRangeKey(startDate, endDate);
        if (rangeKey === "today" || rangeKey === "last30days" || rangeKey === "last7days") {
            return "text-primary-500 font-bold";
        }
        return "text-mute-400";
    };

    return (
        <>
            <HeaderBar />

            <div className="flex flex-col">
                <div className="flex flex-row">
                    <Sidebar />

                    <main className="page">
                        <div className="page-content">
                            <div className="flex flex-col md:flex-row justify-between md:items-center">
                                <h2>{t("pages.siteStatistics")}</h2>

                                {/* DATE PICKERS */}
                                <div className="flex flex-wrap gap-2 mt-2 md:mt-0 min-w-0">
                                    <CustomDropdown
                                        isRight={true}
                                        ContentComponent={() => (
                                            <div className="w-40 flex flex-col gap-2 items-start bg-white p-4 rounded-lg">
                                                <button
                                                    className="text-sm hover:font-bold text-primary-500 hover:text-primary-600"
                                                    onClick={() => {
                                                        const today = new Date();
                                                        setStartDate(today);
                                                        setEndDate(today);
                                                    }}
                                                >
                                                    {t("dateRange.today")}
                                                </button>

                                                <button
                                                    className="text-sm hover:font-bold text-primary-500 hover:text-primary-600"
                                                    onClick={() => {
                                                        const lastWeek = new Date();
                                                        lastWeek.setDate(lastWeek.getDate() - 7);
                                                        setStartDate(lastWeek);
                                                        setEndDate(new Date());
                                                    }}
                                                >
                                                    {t("dateRange.last7Days")}
                                                </button>

                                                <button
                                                    className="text-sm hover:font-bold text-primary-500 hover:text-primary-600"
                                                    onClick={() => {
                                                        const lastMonth = new Date();
                                                        lastMonth.setMonth(lastMonth.getMonth() - 1);
                                                        setStartDate(lastMonth);
                                                        setEndDate(new Date());
                                                    }}
                                                >
                                                    {t("dateRange.last30Days")}
                                                </button>
                                            </div>
                                        )}
                                    >
                                        <div className="flex gap-2">
                                            <button className={`text-sm text-black ${getDaySelectionClass()}`}>
                                                {t(getFriendlyDateRangeI18nKey(startDate, endDate))}
                                            </button>
                                        </div>
                                    </CustomDropdown>

                                    <CustomDropdown
                                        isRight={true}
                                        ContentComponent={() => (
                                            <DatePicker
                                                startDate={startDate}
                                                endDate={endDate}
                                                setStartDate={setStartDate}
                                                setEndDate={setEndDate}
                                            />
                                        )}
                                    >
                                        <div className="flex gap-2">
                                            <button className="text-sm font-normal text-black">
                                                {getDateRange(startDate, endDate, t)}{" "}
                                                <FontAwesomeIcon icon={faChevronDown} />
                                            </button>
                                        </div>
                                    </CustomDropdown>
                                </div>
                            </div>

                            <SiteStatisticsFilter
                                onChangeSitePublicIds={setSitePublicIds}
                                onLoadingChange={setIsLoadingSites}
                                startDate={startDate}
                                endDate={endDate}
                            />

                            <SiteStatisticsContent
                                sitePublicIds={sitePublicIds}
                                startDate={startDate}
                                endDate={endDate}
                                isFilterLoading={isLoadingSites}
                            />
                            </div>
                    </main>
                </div>
            </div>
        </>
    );
}